import { ReactElement, useCallback } from 'react';
import { enrollFormRedirection, IUserCard, toEnrollFormSection } from '.';
import { NavLink, useNavigate } from 'react-router-dom';
import { convertISODateTODDMMYY } from '@helper/utils';
import { useCloseLeadsPopup } from '@store/closeLeadsPopupStore';
import { closeLeadsDropDown } from '@config/constant';
import { useEndUserStore } from '@store/enduser';
import { useStoreFormikFormValue } from '@store/setFormikFormValueStore';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { useIsImpersonationLoginUser } from '../../../../authGuard/hooks/useIsImpersonationLoginUser';

export const UserCard = (props: IUserCard): ReactElement => {
	const { index, state, userObject } = props;
	const userRoleType = useUserRoleType();
	const rawKycStatus = 'kyc_id_proof_verification' in userObject ? userObject.kyc_id_proof_verification : undefined;
	const normalizedKycStatus = typeof rawKycStatus === 'string' ? rawKycStatus.trim().toLowerCase() : '';
	const isKycVerified = normalizedKycStatus === 'verified';
	const isKycPending = normalizedKycStatus === 'not-verified' || normalizedKycStatus === 'not verified';

	const priorityClassName =
		userObject.priority && userObject.priority >= 1 && userObject.priority <= 4
			? `priority-tag-${userObject.priority}`
			: 'priority-tag-4';
	const { setModal, setDropDownData } = useCloseLeadsPopup((state) => state);
	const navigate = useNavigate();
	const {
		setActiveNavTab,
		setIsFormEdit,
		setFormId,
		setFormRevalidate,
		setSelectSubContractor: setStoreValue,
	} = useEndUserStore();
	const { resetFormikValuesToStore } = useStoreFormikFormValue();
	const isImpersonationLoginUser = useIsImpersonationLoginUser();

	const handleNavigate = useCallback(() => {
		const pageRouteName = userRoleType === 'RBDM' ? '/rbdm/contractor' : '/contractor';

		navigate(pageRouteName, {
			state: {
				userData: userObject,
				path: state,
			},
		});
	}, [navigate, state, userObject, userRoleType]);

	const handleEnrollClick = useCallback((): void => {
		if (userObject?.id && userObject?.status) {
			setFormId(userObject.id);
			setIsFormEdit(false);
			setFormRevalidate(false);
			setStoreValue?.([]);
			resetFormikValuesToStore();

			const section = toEnrollFormSection(userObject?.status === 'Pending' ? 'Basic Details' : userObject?.status);
			if (section && enrollFormRedirection[section]) {
				navigate(enrollFormRedirection[section].path);
				setActiveNavTab(enrollFormRedirection[section].activeTabs);
			}
		}
	}, [navigate, setActiveNavTab, setFormId, setFormRevalidate, setIsFormEdit, userObject]);

	const kycStatusPill =
		isKycVerified || isKycPending ? (
			<span className={`h5 kyc-status ${isKycVerified ? 'verified' : 'pending'}`}>
				{isKycVerified ? 'KYC Verified' : 'KYC Pending'}
			</span>
		) : null;

	return (
		<li key={index} className="d-col d-col-2">
			{state === '/new-leads' ? (
				<fieldset>
					<legend title={userObject.name}>{userObject.name}</legend>

					<div className="user-about-details leads-card">
						<div className="card-upper-details">
							<NavLink to={`tel:+91${userObject.phone}`} aria-label="call user link" className="call-icon">
								<span className="icon-call"></span>
							</NavLink>
						</div>

						<div className="user-bottom-details row">
							<div className="left-content d-col d-col-6">
								<p className="h5" title={convertISODateTODDMMYY(userObject.createddate)}>
									Creation: <span>{convertISODateTODDMMYY(userObject.createddate)}</span>
								</p>
								<p className="h5" title={`${userObject.source}`}>
									Source: <span>{userObject.source}</span>
								</p>
								{userObject.primarydealercode && (
									<p className="h5" title={`${userObject.primarydealercode}`}>
										Dealer Code: <span>{userObject.primarydealercode}</span>
									</p>
								)}
							</div>
							{!isImpersonationLoginUser && (
								<div className="right-content d-col d-col-6">
									<NavLink
										to="#"
										className="h5"
										aria-label="close lead url"
										onClick={(e) => {
											e.preventDefault();
											setModal(true);
											setDropDownData(
												{ id: userObject.id, name: userObject.name, number: userObject.phone },
												closeLeadsDropDown
											);
										}}
									>
										Close Lead
									</NavLink>
									<NavLink to="" onClick={handleEnrollClick} className="h5" aria-label="contractor page url">
										Enroll
									</NavLink>
								</div>
							)}
						</div>
					</div>
				</fieldset>
			) : (
				<div role="button" tabIndex={0} onClick={handleNavigate} className="user-card-inner-container">
					<fieldset>
						<legend title={userObject.name}>{userObject.name}</legend>
						{(state === '/all' || state === '/end-users') && (
							<span className={`h5 user-id`}>ID: {userObject.membershipno}</span>
						)}
						<div className="user-about-details adopted-list">
							<div className="card-upper-details">
								<span className="club-name" title={userObject.clubname}>
									Club: {userObject.clubname}
								</span>

								<NavLink
									to={`tel:+91${userObject.phone}`}
									aria-label="call user link"
									className="call-icon"
									onClick={(e) => e.stopPropagation()}
								>
									<span className="icon-call"></span>
								</NavLink>
							</div>

							<div className="user-bottom-details row">
								{state === '/end-users' && (
									<>
										<div className="left-content d-col d-col-12">
											{'task_list' in userObject ? (
												userObject?.task_list?.slice(0, 2)?.map((task: string, index: number) => (
													<p key={task + '_' + index} className="h5" title={task}>
														{task}
													</p>
												))
											) : (
												<></>
											)}
										</div>
										<div className="card-footer-row card-footer--adopted d-col d-col-12">
											{kycStatusPill}
											{userObject.contractorclass && (
												<div className="h5 card-bottom-status">
													{userObject.contractorclass} {userObject.contractorconsumption}
												</div>
											)}
											<div className="user-priority">
												{userObject?.visitstatus !== null && (
													<div
														className={`priority-activation ${userObject.visitstatus?.toLowerCase() === 'site visited' ? 'bg-success' : 'bg-danger'}`}
													>
														<span></span>
													</div>
												)}
												<span className={`priority ${priorityClassName} `}>Priority {userObject.priority}</span>
											</div>
										</div>
									</>
								)}

								{state === '/all' && (
									<>
										<div className="left-content d-col d-col-12">
											<p className="h5" title={`${userObject.glue_consumption_per_month}`}>
												Glue Potential: {userObject.glue_consumption_per_month}
											</p>
										</div>
										<div className="card-footer-row card-footer--all d-col d-col-12">
											<p className="h5 all-footer-banking" title={`${userObject.totalbankingQTD}`}>
												Total Banking QTD: {userObject.totalbankingQTD}
											</p>
											<div className="kyc-status-footer">
												<div className="all-footer-kyc">{kycStatusPill}</div>
												<span className="h5 all-footer-added">
													Added: {convertISODateTODDMMYY(userObject.createddate)}
												</span>
											</div>
										</div>
									</>
								)}
							</div>
						</div>
					</fieldset>
				</div>
			)}
		</li>
	);
};
